import { Icon, MenuItem } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { ConflictError } from "apis/errors";
import { ActionPermissionsObject, IThemeStorageModel, WorkspaceResourceType, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import { Breadcrumb } from "js/Components/Breadcrumb";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { app } from "js/namespaces";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { IconMenu, RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { SearchBarContainer, UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { ThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { NoMyResourcesThemeNotice } from "js/react/components/Notice";

const logger = getLogger(LogGroup.TEAMS);

const Container = styled.div`
    background: white;
    padding: 20px;
`;

function TeamSharedThemesPane(props: WorkspaceControllerState) {
    const { resources } = props;


    const [sharedThemes, setSharedThemes] = useState<IThemeStorageModel[]>([]);
    const [fetching, setFetching] = useState(true);

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    useEffect(() => {
        (async () => {
            const sharedThemes = await WorkspaceController.getSharedThemes();
            setSharedThemes(sharedThemes);
            setFetching(false);
        })();
    }, [getObjectHash(resources.filter(resource => resource.resourceType === WorkspaceResourceType.THEME))]);

    const teamThemes: IThemeStorageModel[] = fetching ? [] : sharedThemes.filter(Boolean);

    const handleCreate = () => {
        AppController.navigateToThemeEditor("new-team-theme");
    };

    const handleEdit = (theme: IThemeStorageModel) => {
        AppController.navigateToThemeEditor(theme.id);
    };

    const handleRename = (theme: IThemeStorageModel) => {
        ShowDialog(InputConfirmationDialog, {
            title: "Rename Theme",
            message: null,
            input: {
                value: theme.get("name")
            },
            acceptCallback: async (name: string) => {
                try {
                    await withProgressDialog({
                        title: "Renaming theme...",
                        action: async () => {
                            theme.update({ name: name.trim() });
                            await theme.updatePromise;
                        }
                    });
                } catch (err) {
                    logger.error(err, "[TeamSharedThemesPane] Error renaming theme");

                    ShowErrorDialog({
                        title: "Unable to rename theme",
                        message: err.message
                    });
                }
            },
        });
    };

    const handleDuplicate = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: "Duplicating theme...",
                action: async () => {
                    const name = "Copy of " + theme.get("name");
                    await app.themeManager.createTeamTheme(theme.attributes, name);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error duplicating theme");

            ShowErrorDialog({
                title: "Unable to duplicate theme",
                message: err.message
            });
        }
    };

    const handleDelete = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: "Deleting theme...",
                action: () => app.themeManager.deleteTeamTheme(theme.id)
            });
        } catch (err) {
            if (err instanceof ConflictError) {
                const title = "Sorry you can't delete a Team Theme that is currently being used by presentations.";
                const message = err.message;

                if (theme.get("hidden")) {
                    ShowMessageDialog({
                        title,
                        message
                    });
                    return;
                }

                const willHide = await ShowConfirmationDialog({
                    title,
                    message: message + " If you want to prevent this Team Theme from being used in additional presentations, you can hide it.",
                    okButtonLabel: "Hide",
                });
                if (willHide) {
                    handleToggleHide(theme);
                }

                return;
            }

            logger.error(err, "[TeamSharedThemesPane] Error deleting theme");
            ShowErrorDialog({
                title: "Unable to delete theme",
                message: err.message
            });
        }
    };

    const handleToggleHide = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: theme.get("hidden") ? "Unhiding theme..." : "Hiding theme...",
                action: async () => {
                    theme.update({ hidden: !theme.get("hidden") });
                    await theme.updatePromise;
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error toggling hide theme");

            ShowErrorDialog({
                title: "Unable to toggle hide theme",
                message: err.message
            });
        }
    };

    return (
        <UIPane>
            <SearchBarContainer className="search-container">
                {canManageSharedResources && (
                    <>
                        <Breadcrumb>Team Themes</Breadcrumb>
                        <RoundIconButton onClick={handleCreate}><Icon>add</Icon></RoundIconButton>
                    </>
                )}
                {canUpgradeToManageSharedResources && (
                    <FlexBox center fillWidth padding="40px 0px 0px 0px">
                        <Banner
                            headerText="Unlock Team Themes"
                            bodyText="Upgrade your plan to keep colors, fonts, and logos consistent across presentations."
                            buttonLabel="Upgrade"
                            onButtonClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: "team_themes" }
                                })
                            }
                        />
                    </FlexBox>
                )}
            </SearchBarContainer>
            <UIPaneResultsContainer>
                {canManageSharedResources &&
                    <Container>
                        <FetchingClickShield visible={fetching} backgroundColor="#EEEEEE" />
                        {teamThemes.length > 0 && (
                            <ThumbnailGrid
                                items={teamThemes}
                                columns={4}
                                selectedItem={false}
                                renderThumbnail={(child: { item: IThemeStorageModel }) => {
                                const isDefault = child.item.id === sharedThemes[0]?.id;
                                    return (
                                        <ThemeThumbnail
                                            item={child.item}
                                            isDefault={isDefault && teamThemes.length > 1}
                                            menu={
                                                <IconMenu>
                                                    <MenuItem onClick={() => handleEdit(child.item)}>
                                                        <Icon>edit</Icon>Edit Theme...
                                                    </MenuItem>
                                                    <MenuItem onClick={() => handleRename(child.item)}>
                                                        <Icon>label</Icon>Rename
                                                    </MenuItem>
                                                    <MenuItem onClick={() => handleDuplicate(child.item)}>
                                                        <Icon>content_copy</Icon>Duplicate
                                                    </MenuItem>
                                                    <hr key="divider" />
                                                    {child.item.get("hidden") ? (
                                                        <MenuItem key="unhide" onClick={() => handleToggleHide(child.item)}>
                                                            <Icon>visibility_on</Icon>Show
                                                        </MenuItem>
                                                    ) : (
                                                        <MenuItem key="hide" onClick={() => handleToggleHide(child.item)}>
                                                            <Icon>visibility_off</Icon>Hide
                                                        </MenuItem>
                                                    )}
                                                    {!isDefault && (
                                                        <MenuItem key="delete" onClick={() => handleDelete(child.item)}>
                                                            <Icon>delete</Icon>Delete...
                                                        </MenuItem>
                                                    )}
                                                </IconMenu>
                                            }
                                            onDoubleClick={() => handleEdit(child.item)}
                                        />
                                    );
                                }}
                            />
                        )}
                    </Container>
                }
                {canUpgradeToManageSharedResources && <NoMyResourcesThemeNotice />}
            </UIPaneResultsContainer>
        </UIPane>
    );
}

export default WorkspaceController.withInitializedState(TeamSharedThemesPane);
